<template>
	<li class="bg-white border rounded p-4 flex justify-between">
		<div class="flex items-center gap-2">
			<img
				:src="icon"
				:alt="`${name} logo`"
				class="shrink-0 h-8 w-8 object-contain"
			/>
			<div class="flex flex-col">
				<span class="font-semibold">
					{{ name }}
				</span>
				<div class="flex flex-wrap items-center gap-x-1 text-sm text-gray-500">
					<template v-if="hasActiveConnection">
						<Popover v-if="connectedLenders.length">
							<PopoverTrigger as-child>
								<button
									type="button"
									class="cursor-pointer underline decoration-dotted underline-offset-2 transition-colors hover:text-gray-700"
								>
									{{ connectedLendersLabel }}
								</button>
							</PopoverTrigger>
							<PopoverContent
								align="start"
								:collision-padding="16"
								class="w-64 max-w-[calc(100vw-2rem)] max-h-[min(20rem,calc(100vh-2rem))] overflow-y-auto p-3"
							>
								<div>
									<ul class="space-y-1 text-sm text-gray-600">
										<li v-for="lender in connectedLenders" :key="lender.name">
											{{ getProviderLabel(lender) }}
										</li>
									</ul>
								</div>
							</PopoverContent>
						</Popover>
						<span v-else>{{ connectedLendersLabel }}</span>
						<span>via {{ name }}</span>
					</template>
					<template v-else>
						<Popover v-if="availableLenders.length">
							<PopoverTrigger as-child>
								<button
									type="button"
									class="cursor-pointer underline decoration-dotted underline-offset-2 transition-colors hover:text-gray-700"
								>
									{{ availableLendersLabel }}
								</button>
							</PopoverTrigger>
							<PopoverContent
								align="start"
								:collision-padding="16"
								class="w-64 max-w-[calc(100vw-2rem)] max-h-[min(20rem,calc(100vh-2rem))] overflow-y-auto p-3"
							>
								<div>
									<ul class="space-y-1 text-sm text-gray-600">
										<li v-for="lender in availableLenders" :key="lender.name">
											{{ getProviderLabel(lender) }}
										</li>
									</ul>
								</div>
							</PopoverContent>
						</Popover>
						<span v-else>{{ availableLendersLabel }}</span>
						<span>via {{ name }}</span>
					</template>
				</div>
			</div>
		</div>
		<div
			v-if="hasActiveConnection || editingConnectionNumber"
			class="flex flex-col self-center justify-center relative group w-48"
			@click="editingConnectionNumber = true"
		>
			<span class="text-sm text-gray-500"> Aansluitnummer: </span>
			<AppButton
				v-if="!editingConnectionNumber"
				variant="ghost"
				icon-right="edit"
				class="px-2 py-1 border rounded bg-gray-100 text-gray-600 text-sm text-start justify-between font-normal [&_.ht-icon]:text-[14px] [&_.ht-icon]:text-gray-400 [&:hover_.ht-icon]:text-gray-600"
				@click="editingConnectionNumber = true"
			>
				{{ connection?.connectionNumber }}
			</AppButton>
			<input
				v-else
				ref="input"
				v-model="numberInput"
				type="text"
				class="px-2 py-1 border rounded text-sm"
				@blur="onBlur"
				@keydown.escape="onEscape"
				@keydown.enter="onEnter"
			/>
		</div>
		<div
			v-else
			class="flex flex-col self-center justify-center relative group w-48"
		>
			<AppButton
				variant="primary"
				class="self-end text-sm"
				icon="dashboard_customize"
				@click="editingConnectionNumber = true"
			>
				Koppelen
			</AppButton>
		</div>
	</li>
</template>

<script lang="ts" setup>
import { getProviderLabel } from "@/composables/useProvider";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import type { Schema } from "@loanlink-nl/zero";
import type { Row } from "@rocicorp/zero";
import { computed, nextTick, ref, useTemplateRef, watch } from "vue";
import AppButton from "../AppButton.vue";

const props = defineProps<{
	serviceProvider: Row<Schema["tables"]["hdnServiceProvider"]> & {
		connections: Readonly<
			Row<Schema["tables"]["agencyServiceProviderConnection"]>[]
		>;
		availableProviders: Readonly<
			(Row<Schema["tables"]["hdnProvider"]> & {
				connections: Readonly<Row<Schema["tables"]["hdnLenderConnection"]>[]>;
			})[]
		>;
	};
}>();

const icon = computed(() => {
	return `/logos/${props.serviceProvider.icon}`;
});

const name = computed(() => {
	return props.serviceProvider.name;
});

const emit = defineEmits<{
	(e: "update:connectionNumber", connectionNumber?: string): void;
}>();

const inputEl = useTemplateRef("input");

const connection = computed(() => {
	return props.serviceProvider?.connections[0];
});

const numberInput = ref(connection?.value?.connectionNumber ?? undefined);

const hasActiveConnection = computed(
	() => !!connection.value?.connectionNumber,
);

const connectedLenders = computed(() => {
	return props.serviceProvider.availableProviders
		.filter((lender) => {
			const [connection] = lender.connections;

			return (
				connection?.serviceProviderId === props.serviceProvider.id &&
				(connection?.sendMethod === "service_provider" ||
					!connection?.connectionNumber)
			);
		})
		.toSorted((a, b) => getProviderLabel(a).localeCompare(getProviderLabel(b)));
});

const availableLenders = computed(() => {
	return props.serviceProvider.availableProviders
		.filter((lender) => lender.connections.length === 0)
		.toSorted((a, b) => getProviderLabel(a).localeCompare(getProviderLabel(b)));
});

const connectedLendersLabel = computed(() => {
	if (connectedLenders.value.length === 1) {
		return "1 geldverstrekker aangesloten";
	}

	return `${connectedLenders.value.length} geldverstrekkers aangesloten`;
});

const availableLendersLabel = computed(() => {
	return `${availableLenders.value.length} nieuwe geldverstrekker${availableLenders.value.length === 1 ? "" : "s"} beschikbaar`;
});

let escaped = false;

function onBlur() {
	if (escaped) {
		escaped = false;
		return;
	}

	editingConnectionNumber.value = false;
	updateConnectionNumber(numberInput.value);
}

function onEscape() {
	escaped = true;
	editingConnectionNumber.value = false;
}

function onEnter() {
	onBlur();
}

function updateConnectionNumber(newNumber?: string) {
	emit("update:connectionNumber", newNumber);
}

const editingConnectionNumber = ref(false);
watch(editingConnectionNumber, async () => {
	if (editingConnectionNumber.value) {
		await nextTick(() => {
			inputEl.value?.select();
		});
	}
});
</script>
